import * as React from 'react';
import { useSelector } from 'react-redux';

import { ButtonSizes } from '@Components/Buttons/Button';
import ButtonLink from '@Components/Buttons/ButtonLink';
import OldOrderFormContainer from '@Components/Order/OldOrderFormContainer';
import PreliminaryOrderFormContainer from '@Components/Order/PreliminaryOrderFormContainer';
import Typography from '@Components/Typography';
import { Messages } from '@Config/messages';
import { RouteConfig } from '@Config/routes';
import { AdvertFragment } from '@Graphql/graphqlTypes.generated';
import { useTranslations } from '@Hooks/useTranslations';
import { getFeatureToggle } from '@Store/app/app.selectors';
import { getUserCompanyData, getUserIsLogged } from '@Store/auth/auth.selector';
import { UserActiveCompany } from '@Store/auth/auth.types';
import { UserUrlTypes } from '@Utils/types';

import styles from './AdPageOrderSectionStyles.module.scss';

export interface AdPageOrderSectionProps {
  advert: AdvertFragment;
  onCancel: () => void;
}

const AdPageOrderSection: React.FunctionComponent<AdPageOrderSectionProps> = ({ advert, onCancel }) => {
  const t = useTranslations();
  const company: UserActiveCompany | undefined = useSelector(getUserCompanyData);
  const isLoggedIn: boolean = useSelector(getUserIsLogged);
  const oldOrderForm = useSelector(getFeatureToggle('oldOrderForm'));

  if (company) {
    if (company.company.id === advert.company.id) {
      return (
        <ButtonLink
          link={RouteConfig.AdvertEdit.buildLink({ id: advert.id })}
          label={t(Messages.btnEditAdvert)}
          size={ButtonSizes.fill}
        />
      );
    }
    return oldOrderForm ? (
      <OldOrderFormContainer advert={advert} />
    ) : (
      <PreliminaryOrderFormContainer advertId={advert.id} onCancel={onCancel} />
    );
  }

  if (isLoggedIn && !company) {
    return (
      <>
        <Typography
          msg={t(Messages.adDisplayCreateCompanyFirst)}
          color="gray"
          tag="div"
          className={styles.firstCreateCompanyMessage}
        />
        <ButtonLink
          link={RouteConfig.User.buildLink({ type: UserUrlTypes.company })}
          label={t(Messages.createCompanyButton)}
          size={ButtonSizes.fill}
        />
      </>
    );
  }

  if (!isLoggedIn) {
    return oldOrderForm ? (
      <Typography tag="h4" msg={t(Messages.errorOrderOnlyForLoggedInUsers)} html className={styles.inline} />
    ) : (
      <PreliminaryOrderFormContainer advertId={advert.id} onCancel={onCancel} />
    );
  }
  return null;
};

export default AdPageOrderSection;
